import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { IBetTicketData } from "./IBetTicketData";
import { ISport } from "../Sports/ISport";
import { sessionStore } from "../../utils/storage";
import { BETS_FILTER } from "../../utils/enum";

/**
 * Phase 4e slimmed this slice to UI/client state only. The server
 * data (paginated bets list, reactions, optimistic ticket
 * mutations) all moved to TanStack Query — see src/api/hooks/bets.ts.
 *
 * What remains here:
 *   - betTicketData: the in-progress ticket the user is building.
 *     Persists to sessionStorage so a reload mid-build doesn't wipe
 *     it. Touched from many surfaces (LeagueMatchBet, OddsDialog,
 *     BetTicket) — classic UI state that benefits from a single
 *     Redux source.
 *   - betsSportFilter / betsFilter: feed filter chips. The feed
 *     page reads them and passes them into useInfiniteBets as
 *     queryKey params, so changing them naturally triggers a refetch.
 */

export interface IBetsState {
  betTicketData: IBetTicketData[];
  betsSportFilter: ISport | null;
  betsFilter: string;
  /**
   * Ticket id of the most recently created bet, used to trigger the
   * post-submit Reasoning Capture modal. Lives in Redux (not in
   * `BetTicket`'s local `useState`) because `BetTicket` can be
   * unmounted by parent re-renders during the submit flow — local
   * state would be wiped before the modal renders. Cleared to `null`
   * when the user saves tags or skips.
   */
  reasoningPromptTicketId: number | null;
}

const initialState: IBetsState = {
  betTicketData: [],
  betsSportFilter: null,
  betsFilter: BETS_FILTER.LATEST,
  reasoningPromptTicketId: null,
};

const betsSlice = createSlice({
  name: "bets",
  initialState,
  reducers: {
    setBetTicketData: (state, action: PayloadAction<IBetTicketData[]>) => {
      state.betTicketData = action.payload;
      sessionStore.set("betTicket", action.payload);
    },
    setBetsSportFilter: (state, action: PayloadAction<ISport | null>) => {
      state.betsSportFilter = action.payload;
    },
    setBetsFilter: (state, action: PayloadAction<string>) => {
      state.betsFilter = action.payload;
    },
    setReasoningPromptTicketId: (state, action: PayloadAction<number | null>) => {
      state.reasoningPromptTicketId = action.payload;
    },
  },
});

const { reducer, actions } = betsSlice;
export default reducer;
export const { setBetTicketData, setBetsSportFilter, setBetsFilter, setReasoningPromptTicketId } =
  actions;

export const betsSportFilterSelector = createSelector(
  [(state: { bets: IBetsState }) => state.bets],
  (item) => item?.betsSportFilter,
);

export const betTicketDataSelector = createSelector(
  [(state: { bets: IBetsState }) => state.bets],
  (item) => item?.betTicketData,
);

export const betsFilterSelector = createSelector(
  [(state: { bets: IBetsState }) => state.bets],
  (item) => item?.betsFilter,
);

export const reasoningPromptTicketIdSelector = createSelector(
  [(state: { bets: IBetsState }) => state.bets],
  (item) => item?.reasoningPromptTicketId ?? null,
);
